import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { MessageSquare, X, Send } from "lucide-react";
import { cn } from "@/lib/utils";

const CATEGORIES = ["Yem/Su", "İlaçlama", "Sağlık/Kritik", "Genel Not"];
const SEV_DOT = { danger: "bg-destructive", warning: "bg-amber-500", info: "bg-sky-500" };

export default function ChatWidget() {
  const { coops, myCoops, isOperator } = useApp();
  const subCoops = myCoops.filter((c) => c.type === "coop");
  const [open, setOpen] = useState(false);
  const [coopId, setCoopId] = useState("");
  const [category, setCategory] = useState("Yem/Su");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);
  const feedRef = useRef(null);

  const coopName = (id) => coops.find((c) => c.id === id)?.name || "?";

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (open && feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [items, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!coopId) return toast.error("Lütfen kümes seçin");
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/notifications", { coop_id: coopId, category, message: text.trim() });
      setText("");
      if (category === "Sağlık/Kritik") toast.success("Kritik bildirim gönderildi — yöneticiye e-posta iletildi");
      else toast.success("Bildirim gönderildi");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Gönderilemedi"));
    } finally {
      setSending(false);
    }
  };

  const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="fixed bottom-safe-5 right-5 z-[60] flex flex-col items-end" data-testid="chat-widget">
      {open && (
        <div className="mb-3 w-[340px] max-w-[calc(100vw-2.5rem)] h-[440px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-foreground text-background">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              <strong className="text-sm font-head">Kümes Durum Bildirimi</strong>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-background/60 hover:text-background transition-colors"><X size={18} /></button>
          </div>

          {/* Feed */}
          <div ref={feedRef} className="flex-1 p-3 bg-secondary/30 overflow-y-auto space-y-2" data-testid="chat-feed">
            <div className="rounded-lg bg-secondary text-muted-foreground text-xs px-3 py-2 max-w-[90%]">
              Kümes seçip anlık durum notu, yem uyarısı veya operasyon bilgisi iletebilirsiniz.
            </div>
            {items.map((m) => (
              <div key={m.id} className="rounded-lg bg-card border border-border px-3 py-2 max-w-[92%]" data-testid={`chat-msg-${m.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground mb-1">
                  <span className={cn("h-1.5 w-1.5 rounded-full", SEV_DOT[m.severity] || "bg-sky-500")} />
                  {coopName(m.coop_id)} • {m.category} • {timeOf(m.created_at)}
                </div>
                <div className="text-[13px] leading-snug">{m.message}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{m.created_by_name}</div>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={submit} className="p-3 border-t border-border bg-card space-y-2" data-testid="chat-form">
            <div className="flex gap-2">
              <select value={coopId} onChange={(e) => setCoopId(e.target.value)} required data-testid="chat-coop"
                className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs focus:ring-2 focus:ring-primary outline-none">
                <option value="" disabled>Kümes Seçin</option>
                {subCoops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="chat-category"
                className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs focus:ring-2 focus:ring-primary outline-none">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Durum bilgisini yazın..." data-testid="chat-input"
                className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
              <button type="submit" disabled={sending} data-testid="chat-send"
                className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-1">
                <Send size={14} /> Gönder
              </button>
            </div>
          </form>
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} data-testid="chat-toggle"
        className="h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 hover:scale-105 transition-all">
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>
    </div>
  );
}
